// Step validation utilities for draft restoration

export const getStepProgress = (draftData) => {
  if (!draftData) return { completedSteps: [], currentStep: 0 };

  const completedSteps = [];
  let currentStep = 0;

  // Step 0 validation - Customer Details
  const hasCustomerDetails = 
    draftData.formData?.statusOfCustomer && 
    draftData.formData?.gstStatus &&
    (draftData.formData?.gstStatus === 'Un-Registered' || draftData.formData?.gstRegistrationType);

  if (hasCustomerDetails) {
    completedSteps.push(0);
    currentStep = 1;
  }

  // Step 1 validation - Document Uploads
  const hasRequiredDocuments = checkDocumentRequirements(draftData);
  
  if (hasRequiredDocuments && completedSteps.includes(0)) {
    completedSteps.push(1);
    currentStep = 2;
  }

  // Step 2 validation - Review (no specific validation needed)
  if (completedSteps.includes(1)) {
    completedSteps.push(2);
    currentStep = 3;
  }

  // Step 3 validation - Additional Information
  const hasAdditionalInfo = 
    draftData.additional?.email &&
    draftData.additional?.mobile &&
    draftData.additional?.contactName &&
    draftData.additional?.contactMobile &&
    draftData.additional?.certifyTrue &&
    draftData.additional?.acceptPrivacy;

  if (hasAdditionalInfo && completedSteps.includes(2)) {
    completedSteps.push(3);
    currentStep = 4; // All steps completed
  }

  return { 
    completedSteps, 
    currentStep: Math.min(currentStep, draftData.activeStep || 0),
    totalSteps: 4
  };
};

const checkDocumentRequirements = (draftData) => {
  const { formData, fileLinks, extractedDetails } = draftData;
  
  if (!formData) return false;

  // Helper functions for document validation
  const hasDocument = (fileLink) => Boolean(fileLink);
  const hasDocumentWithData = (fileLink, ocrData) => Boolean(fileLink && ocrData);

  const isProprietorUnreg = 
    formData.statusOfCustomer === 'Proprietor / Individual' &&
    formData.gstStatus === 'Un-Registered';

  const isPrivateReg = 
    formData.statusOfCustomer === 'Private Limited/ Public Limited';

  const isForeignEntity = formData.statusOfCustomer === 'Foreign Entity';

  if (isForeignEntity) {
    return hasDocument(fileLinks?.swiftFileLink);
  }

  if (isProprietorUnreg) {
    return (
      hasDocument(fileLinks?.declarationFileLink) &&
      hasDocumentWithData(fileLinks?.panFileLink, extractedDetails?.panDetails?.panNumber) &&
      draftData.altDocType &&
      hasDocument(fileLinks?.altDocFileLink) &&
      (
        (hasDocument(fileLinks?.chequeFileLink) && extractedDetails?.chequeDetails?.ifsc) ||
        (draftData.bankDetails?.ifsc && (!draftData.hasNoCheque || hasDocument(fileLinks?.mandateFileLink)))
      )
    );
  }

  if (isPrivateReg) {
    return (
      hasDocumentWithData(fileLinks?.gstFileLink, extractedDetails?.gstDetails?.registrationNumber) &&
      hasDocumentWithData(fileLinks?.panFileLink, extractedDetails?.panDetails?.panNumber) &&
      hasDocumentWithData(fileLinks?.tanFileLink, extractedDetails?.tanDetails?.tanNumber) &&
      hasDocument(fileLinks?.incFileLink) &&
      (
        (hasDocument(fileLinks?.chequeFileLink) && extractedDetails?.chequeDetails?.ifsc) ||
        (draftData.bankDetails?.ifsc && (!draftData.hasNoCheque || hasDocument(fileLinks?.mandateFileLink)))
      )
    );
  }

  // Default validation for other types
  return (
    hasDocumentWithData(fileLinks?.gstFileLink, extractedDetails?.gstDetails?.registrationNumber) &&
    hasDocumentWithData(fileLinks?.panFileLink, extractedDetails?.panDetails?.panNumber) &&
    (
      (hasDocument(fileLinks?.chequeFileLink) && extractedDetails?.chequeDetails?.ifsc) ||
      (draftData.bankDetails?.ifsc && (!draftData.hasNoCheque || hasDocument(fileLinks?.mandateFileLink)))
    )
  );
};

export const getProgressSummary = (draftData) => {
  const { completedSteps, currentStep, totalSteps } = getStepProgress(draftData);
  
  const progress = {
    percentage: Math.round((completedSteps.length / totalSteps) * 100),
    completedCount: completedSteps.length,
    totalCount: totalSteps,
    nextStepName: getStepName(currentStep),
    summary: []
  };

  // Add summary of what was completed
  if (completedSteps.includes(0)) {
    progress.summary.push(`✅ Customer details: ${draftData.formData?.statusOfCustomer}`);
  }
  
  if (completedSteps.includes(1)) {
    const docCount = Object.values(draftData.fileLinks || {}).filter(link => link).length;
    progress.summary.push(`✅ Documents uploaded: ${docCount} files`);
  }
  
  if (completedSteps.includes(2)) {
    progress.summary.push(`✅ Information reviewed`);
  }
  
  if (completedSteps.includes(3)) {
    progress.summary.push(`✅ Additional information completed`);
  }

  return progress;
};

const getStepName = (stepIndex) => {
  const stepNames = [
    'Customer Details',
    'Document Uploads', 
    'Review',
    'Additional Information'
  ];
  return stepNames[stepIndex] || 'Submission Ready';
};

export const shouldSkipToStep = (draftData, targetStep) => {
  const { completedSteps } = getStepProgress(draftData);
  return completedSteps.includes(targetStep - 1);
};